import { ArrowDownAZ, Download, Eye, EyeOff, Info, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenuItem,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { FILTER_LOCK_TOOLTIP } from '../../model/assetViewScope';
import { VISIBILITY_COPY } from '../../visibility/visibilityCopy';
import RequestSupportMenuItem from './RequestSupportMenuItem';
import { usePhotosActions } from './usePhotosActions';
import { useImageDetailsToggle } from './useImageDetailsToggle';

type PhotosMenuItemsProps = {
  setShowDownloadModal: (show: boolean) => void;
  onOpenSupportRequest: () => void;
  /** Withheld by the page when the user may not upload here (DV-2820). */
  onOpenUpload?: () => void;
  /**
   * Force-disables every item EXCEPT Request Support. Zillow 3D renders the
   * Photos-shaped kebab inert rather than removed (QA-round layout-shift
   * decision), but support must stay reachable there — so the trigger itself
   * is no longer disabled (QA round 3, 2026-08-19); only the content is.
   */
  disabled?: boolean;
  /**
   * DV-2861: the client-visibility item. Withheld by the page unless this
   * user may change it here (internal role, a loaded photoset); absent means
   * no item, the same idiom as onOpenUpload.
   */
  visibility?: { visible: boolean; onOpen: () => void };
  /**
   * DV-2859 / DV-3537: the internal-only Caption Sort item. Withheld by the
   * page for roles that may not sort; absent means no item. `onSelect` goes
   * through the page's missing-caption check (useCaptionSortPrompt), which
   * owns the warning dialog — it has to outlive this menu. Internal PP gets
   * the item only below the desktop breakpoint, as the mirror of their
   * toolbar button; the page withholds it at desktop (from the same media
   * query the control bar uses, not CSS), and passes `internalBadge: false`.
   */
  captionSort?: {
    onSelect: () => void;
    disabled: boolean;
    internalBadge: boolean;
  };
};

// AssetsControlBar's `menu` slot for the Photos tab (the kebab's contents):
// sub-lg-first Request Support, then the two view-filter toggles Show Hidden
// Photos (2026-08-19, Task 6 + QA round 3) and Show Image Details (DV-2856)
// kept adjacent, then the internal-only Caption Sort (DV-2859, badged
// "Internal"; for Internal PP only below desktop and unbadged, DV-3537), then the sub-lg-only Download/Upload mirrors of
// PhotosLeadingActions's icon buttons. Hide Selected, Stage with AI and
// Delete Selected all moved out to the floating selection bar
// (AssetsSelectionBar).
// Last of all, at every width, the internal-only client-visibility item
// (DV-2861, badged "Internal") — "bottom of the menu no matter what" (Tim,
// 2026-09-16).
//
// Keep this list current: it is the only inventory of the menu, and a stale
// one is what the PR #1154 review caught (it still claimed the August kebab).
const PhotosMenuItems = ({
  setShowDownloadModal,
  onOpenSupportRequest,
  onOpenUpload,
  disabled = false,
  visibility,
  captionSort
}: PhotosMenuItemsProps) => {
  const {
    editEnabled,
    showHidden,
    hiddenCount,
    hasPhotos,
    filtersLocked,
    downloadLabel,
    setShowHiddenPhotos
  } = usePhotosActions();
  const { showImageDetails, setShowImageDetails } = useImageDetailsToggle();

  // Show Hidden is a view filter, so a selection freezes it (decided
  // 2026-09-18) — but NOT via `disabled`: the primitive sets
  // pointer-events-none on a disabled item, which would eat the hover that
  // explains the freeze. aria-disabled + a prevented onSelect is the same
  // contract to a screen reader and to a click, and it keeps the menu open
  // rather than acting.
  const showHiddenItem = (
    <DropdownMenuItem
      disabled={disabled}
      // `disabled` is folded in by hand: an explicit aria-disabled prop
      // overrides the one the primitive derives from it.
      aria-disabled={disabled || filtersLocked || undefined}
      className={cn(filtersLocked && 'cursor-not-allowed opacity-45')}
      onSelect={(event) => {
        if (filtersLocked) {
          event.preventDefault();
          return;
        }
        setShowHiddenPhotos(!showHidden);
      }}
    >
      <Eye className="w-4 h-4" />
      {showHidden ? 'Hide Hidden Photos' : 'Show Hidden Photos'}
      {hiddenCount > 0 ? ` (${hiddenCount})` : ''}
    </DropdownMenuItem>
  );

  // Its own provider: this menu is rendered into a portal and is mounted on
  // its own in tests, so it cannot rely on the control bar's.
  const lockedShowHiddenItem = (
    <TooltipProvider>
      <Tooltip delayDuration={250}>
        <TooltipTrigger asChild>{showHiddenItem}</TooltipTrigger>
        <TooltipContent side="left">{FILTER_LOCK_TOOLTIP}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <>
      <RequestSupportMenuItem onOpenSupportRequest={onOpenSupportRequest} />
      <DropdownMenuSeparator className="lg:hidden" />
      {/* Show Hidden Photos' gate is editEnabled (spec §1.7, unchanged from
          the old bar) — currently a no-op since canUseEditSuite() is
          constant-true, but the gating table is the contract. */}
      {editEnabled && (filtersLocked ? lockedShowHiddenItem : showHiddenItem)}
      {/* DV-2856. Deliberately NOT inside the editEnabled gate above: image
          details are client-facing (decided 2026-08-12), so a Customer Admin
          sees the same row a post-processing editor does. Exempt from the
          selection lock too: it changes what is printed on each card, never
          which cards render, so it cannot move the pool under a selection.
          Kept adjacent to Show Hidden Photos: both are view filters. */}
      <DropdownMenuItem
        disabled={disabled}
        onSelect={() => setShowImageDetails(!showImageDetails)}
      >
        <Info className="w-4 h-4" />
        {showImageDetails ? 'Hide Image Details' : 'Show Image Details'}
      </DropdownMenuItem>
      {/* DV-2859: whole-set action, so it is NOT behind the selection lock —
          it re-sequences every photo regardless of what is checked (AC), and
          the selection survives because items keep their identity. Never
          hidden or disabled by photo count either: an empty or one-photo set
          is the server's no-op, not a UI state. Disabled only while a sort is
          in flight, so a double click cannot queue a second toast.
          DV-3537: no "Internal" badge on Internal PP's sub-lg mirror — their
          toolbar button carries none either. */}
      {captionSort && (
        <DropdownMenuItem
          disabled={disabled || captionSort.disabled}
          onSelect={captionSort.onSelect}
        >
          <ArrowDownAZ className="w-4 h-4" />
          Caption Sort
          {captionSort.internalBadge && (
            <Badge variant="secondary">Internal</Badge>
          )}
        </DropdownMenuItem>
      )}
      {/* Below lg, Download/Upload have no room in the leading cluster
          (PhotosLeadingActions hides them via max-lg:hidden) — they mirror
          here instead, lg:hidden so they don't double up once there's room.
          The separator itself is lg:hidden too, so desktop's kebab (which
          hides both mirror items) doesn't get a dangling trailing divider. */}
      <DropdownMenuSeparator className="lg:hidden" />
      <DropdownMenuItem
        className="lg:hidden"
        disabled={disabled || !hasPhotos}
        onSelect={() => setShowDownloadModal(true)}
      >
        <Download className="w-4 h-4" />
        {downloadLabel}
      </DropdownMenuItem>
      {editEnabled && onOpenUpload && (
        <DropdownMenuItem
          className="lg:hidden"
          disabled={disabled}
          onSelect={onOpenUpload}
        >
          <Upload className="w-4 h-4" />
          Upload Photos
        </DropdownMenuItem>
      )}
      {/* DV-2861: last, below everything, at every width — so its separator
          is not lg:hidden like the mirrors' above. Exempt from `disabled`
          like Request Support: it acts on the whole photoset, not on the
          Zillow tab's contents. Not behind the selection lock either — a
          whole-set action, like Caption Sort. */}
      {visibility && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={visibility.onOpen}>
            {visibility.visible ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            {visibility.visible
              ? VISIBILITY_COPY.menuHide
              : VISIBILITY_COPY.menuMakeVisible}
            <Badge variant="secondary">Internal</Badge>
          </DropdownMenuItem>
        </>
      )}
    </>
  );
};

export default PhotosMenuItems;
